import {useEffect, useState, useRef, useMemo} from 'react';
import {Product} from '@amplify/athena-models';
import {makeStyles} from '@material-ui/core/styles';
import {savePrequalRecommendations} from './savePrequalRecommendations';
import {
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Typography,
    Button as MUIButton,
} from '@material-ui/core';
import {IS_PRODUCTION} from '../../constants';
import {observer} from 'mobx-react-lite';
import ClientGoals from './Components/ClientGoals';
import BasicInfo from './Components/BasicInfo';
import Behavior from './Components/Behavior';
import Finances from './Components/Finances';
import HealthInfo from './Components/Health';
import {ClientQuoteData} from '../../APIs/CRM';
import {Parameter, ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import AgentAssistPanel from '../Assist/AgentAssistPanel';
import {Button} from '@/components/ui/button';
import RootStore from '@/Core/mobx/RootStore';
import {SsnForm} from '@/APIs/types';
import {generatePath, useHistory, useParams, useRouteMatch} from 'react-router-dom';
import {fieldOptions} from '../../Core/mobx/ParameterStore/PrequalXref';
import {SelectInputElement2} from './Components/SelectInputElement2';
import {BestFitProduct} from './Components/BestFitProduct';
import {WalkthroughRecommendationSummary} from './Components/WalkthroughRecommendationSummary';
import {determineProductTypeRecommendations} from './utils/recommendationLogic';
import {useCurrentUserSettings} from '@/Core/hooks/useCurrentUserSettings';

interface PrequalPanelParams {
    customerQuoteData?: ClientQuoteData[];
    valueChanged: (p?: Parameter, value?: string | number | null) => void;
    parameterStore: ParameterStore;
    contactsFormStore: typeof RootStore.agentPortalStore.contactsFormStore;
}

interface AllProductType extends Product {
    recommended?: boolean;
}

const useStyles = makeStyles({
    recommendationTitle: {
        fontSize: 18,
        fontWeight: 500,
        color: '#838383',
    },
});

function filterQualifiedProducts(products: AllProductType[]) {
    return products.filter(
        (product) =>
            !['AM', 'MOO'].includes(product.carrierCode) &&
            !['OMEXPRIUL', 'LSWIUL', 'LSWIULPRF', 'LSWTERM', 'LSWTERMPRF'].includes(product.code),
    );
}

interface MatchParams {
    tabIndex: string;
}

const FORM_TYPE_MAPPING: Record<string, 'prequal' | 'quote' | 'preapproval'> = {
    '0': 'prequal',
    '1': 'quote',
    '2': 'prequal',
    '3': 'preapproval',
};

let resolveConfirmSave: (confirm: boolean) => void;

export const PrequalPanel = observer(
    ({valueChanged, customerQuoteData, parameterStore, contactsFormStore}: PrequalPanelParams) => {
        const {prequal, health, walkthroughData} = parameterStore || {};
        const {historyFormId} = useParams<any>();
        const {useMultiPagePrequal} = useCurrentUserSettings();
        const warningOffset =
            (!IS_PRODUCTION ? 76 : 0) +
            (parameterStore != null && !RootStore.currentAgentStates.includes(parameterStore.approval.state.value)
                ? 80
                : 0);
        const [loading, setLoading] = useState(false);
        const [saveConfirmationOpen, setSaveConfirmationOpen] = useState(false);
        const [duplicateSsnForms, setDuplicateSsnForms] = useState<SsnForm[]>([]);
        const [assistPanelExpanded, setAssistPanelExpanded] = useState(true);
        const match = useRouteMatch<MatchParams>();
        const history = useHistory();

        const formType = FORM_TYPE_MAPPING[match.params.tabIndex];

        const confirmSave = async (): Promise<boolean> => {
            const response = await new Promise<boolean>((resolve) => {
                resolveConfirmSave = resolve;
                setSaveConfirmationOpen(true);
            });

            setSaveConfirmationOpen(false);

            return response;
        };

        const confirmPendingSave = () => {
            resolveConfirmSave(true);
        };

        const cancelPendingSave = () => {
            resolveConfirmSave(false);
        };

        const saveForm = async (selectedProduct?: string) => {
            setLoading(true);

            // Save the selected product if provided
            if (selectedProduct && prequal?.selectedProduct) {
                prequal.selectedProduct.setValue(selectedProduct);
            }

            const formsWithSameSsn =
                await RootStore.agentPortalStore.contactsFormStore.getSsnFormsAssociatedWithCurrentFormSsn();
            const duplicateSsnForms = formsWithSameSsn.filter((form) => form.formId !== parameterStore.formid);

            if (duplicateSsnForms.length > 0) {
                setDuplicateSsnForms(duplicateSsnForms);
                if (!(await confirmSave())) {
                    setLoading(false);
                    return;
                }
            }

            const form = await contactsFormStore.saveForm(formType);
            const newPath = generatePath('/agent/form/:email/:formId/:tabIndex', {
                email: form?.contact,
                formId: form?.formid,
                tabIndex: match.params.tabIndex,
            });
            window.history.replaceState(null, '', newPath);
            history.push(newPath);
            RootStore.uiStore.showSnackbar('Form successfully saved', 'success');
            setLoading(false);
        };
        const qualifiedProducts = parameterStore.qualifiedProducts;
        const recommendedProductsResponse = parameterStore.recommendedProductsResponse;
        const [iulProducts, vulProducts, termProducts, finalExpenseProducts, annuityProducts, adbProducts] = useMemo(
            () =>
                ['IUL', 'VUL', 'TERM', 'FEXP', 'ANNUITY', 'ADB'].map((type) =>
                    qualifiedProducts?.filter((rp) => rp.productTypeCode === type),
                ),
            [qualifiedProducts],
        );

        // Separate useEffect to handle saving recommendations
        const lastSavedRecommendation = useRef<string>('');
        const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
        const primaryRecommendationValue = useMemo(() => {
            return prequal?.recommendedProductPrimary?.value ?? null;
        }, [prequal?.recommendedProductPrimary?.value]);

        const fallbackRecommendationValue = useMemo(() => {
            return prequal?.recommendedProductFallback?.value ?? null;
        }, [prequal?.recommendedProductFallback?.value]);

        useEffect(() => {
            // Only save if we have recommendations
            if (!primaryRecommendationValue) return;
            const email = parameterStore?.contact || parameterStore?.walkthroughData?.email;
            const customerId = parameterStore?.formid || '';
            // Create a unique key for this recommendation
            const recommendationKey = `${primaryRecommendationValue}-${fallbackRecommendationValue || ''}`;
            // Skip if same as last saved
            if (recommendationKey === lastSavedRecommendation.current) {
                return;
            }
            // Clear existing timeout
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            // Debounce the save
            saveTimeoutRef.current = setTimeout(() => {
                if (!email) {
                    return;
                }
                const prequalParameters = {
                    age: prequal.age?.value,
                    gender: prequal.gender?.value,
                    state: prequal.state?.value,
                    tobacco: prequal.tobacco?.value,
                    height: prequal.height?.value,
                    weight: prequal.weight?.value,
                    incomeHouseholdText: prequal.incomeHouseholdText?.value,
                    coverageAmount: prequal.recommendedCoverageAmount?.value,
                    monthlyBudget: prequal.monthlyBudget?.value,
                    intentFocus: prequal.intentFocus?.value,
                    intentFunding: prequal.intentFunding?.value,
                    intentBenefitStart: prequal.intentBenefitStart?.value,
                };
                savePrequalRecommendations({
                    email,
                    customerId,
                    primaryRecommendation: primaryRecommendationValue,
                    primaryScript: prequal.recommendedProductPrimaryScript?.value,
                    fallbackRecommendation: fallbackRecommendationValue || '',
                    fallbackScript: prequal.recommendedProductFallbackScript?.value,
                    fallbackTriggers: prequal.recommendedProductFallbackTriggers?.value,
                    ineligibleForOriginal:
                        prequal.recommendedProductIneligibleForOriginalRecommendations?.value === 'true',
                    prequalParameters,
                    intentFocus: prequal.intentFocus?.value,
                    householdIncome: prequal.incomeHouseholdText?.value
                        ? Number(prequal.incomeHouseholdText?.value)
                        : undefined,
                    age: prequal.age?.value,
                });
                lastSavedRecommendation.current = recommendationKey;
            }, 3000);
            // Cleanup
            return () => {
                if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                }
            };
        }, [primaryRecommendationValue, fallbackRecommendationValue]);

        const intentFocusValue = useMemo(() => prequal?.intentFocus?.value ?? '', [prequal?.intentFocus?.value]);
        const intentFundingValue = useMemo(() => prequal?.intentFunding?.value ?? '', [prequal?.intentFunding?.value]);
        const intentBenefitStartValue = useMemo(
            () => prequal?.intentBenefitStart?.value ?? '',
            [prequal?.intentBenefitStart?.value],
        );
        const ageValue = useMemo(() => prequal?.age?.value ?? 0, [prequal?.age?.value]);
        const incomeHouseholdTextValue = useMemo(
            () => prequal?.incomeHouseholdText?.value ?? '',
            [prequal?.incomeHouseholdText?.value],
        );
        const recommendedCoverageAmountValue = useMemo(
            () => prequal?.recommendedCoverageAmount?.value ?? '',
            [prequal?.recommendedCoverageAmount?.value],
        );

        useEffect(() => {
            // Only run if we have the necessary data
            if (!recommendedProductsResponse) {
                return;
            }

            const eligibleCodes = recommendedProductsResponse?.allEligibleProductCodes || [];

            // Filter each product type array to only include eligible products
            const filterEligible = (products: AllProductType[] | undefined) => {
                if (!products) return [];
                const filtered = filterQualifiedProducts(products);
                return filtered.filter((p) => eligibleCodes.includes(p.code));
            };

            // Use the factored recommendation logic with filtered eligible products
            const productEligibility = {
                iulProducts: filterEligible(iulProducts),
                termProducts: filterEligible(termProducts),
                vulProducts: filterEligible(vulProducts),
                finalExpenseProducts: filterEligible(finalExpenseProducts),
                annuityProducts: filterEligible(annuityProducts),
                adbProducts: filterEligible(adbProducts),
            };

            determineProductTypeRecommendations(prequal, productEligibility);
        }, [
            intentFocusValue,
            intentFundingValue,
            intentBenefitStartValue,
            ageValue,
            incomeHouseholdTextValue,
            recommendedCoverageAmountValue,
            iulProducts,
            vulProducts,
            termProducts,
            finalExpenseProducts,
            annuityProducts,
            adbProducts,
            qualifiedProducts,
            parameterStore.recommendedProductsResponse,
        ]);

        const classes = useStyles();
        const gridContainerStyle = useMemo(
            () => ({height: `calc(100vh - ${120 + warningOffset}px)`, overflowY: 'hidden' as const}),
            [warningOffset],
        );

        const required = (
            <span>
                <span style={{color: 'red'}}>*</span>
            </span>
        );

        if (parameterStore === null) return null;

        const investmentGoals = parameterStore.walkthroughData?.investmentGoals || [];

        return (
            <Grid container spacing={2} style={gridContainerStyle}>
                <Grid item xs={assistPanelExpanded ? 8 : 11} style={{height: '100%', overflowY: 'auto'}}>
                    <Card style={{padding: 20, marginBottom: 20}}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: '#FFF0F0',
                                borderRadius: 6,
                                padding: '4px 0',
                            }}
                        >
                            <span style={{height: 1, backgroundColor: '#E53935', flex: 1, marginLeft: 16}} />
                            <p
                                style={{
                                    color: '#C62828',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    padding: '12px 18px',
                                    textTransform: 'uppercase',
                                    fontSize: 14,
                                    letterSpacing: '0.05em',
                                }}
                            >
                                We&apos;re on a recorded line
                            </p>
                            <span style={{height: 1, backgroundColor: '#E53935', flex: 1, marginRight: 16}} />
                        </div>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <ClientGoals investmentGoals={investmentGoals} />
                            </Grid>
                        </Grid>

                        <WalkthroughRecommendationSummary
                            parameterStore={parameterStore}
                            customerQuoteData={customerQuoteData}
                        />

                        <CardContent>
                            <div>
                                <strong className={classes.recommendationTitle}>Questions</strong>
                            </div>
                            <Grid container>
                                <Grid item xs={8}>
                                    <Typography>
                                        If you had to choose one, would you say your focus is more on saving for long
                                        term, getting retirement income in the next 15 years, maximizing life insurance
                                        protection right now, or passing down an inheritance?
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <SelectInputElement2
                                        param={{
                                            label: 'Main Focus',
                                            options: fieldOptions.IntentFocus.options,
                                            optionValues: fieldOptions.IntentFocus.optionValues,
                                            ...prequal.intentFocus,
                                            name: 'intentFocus',
                                        }}
                                        required={required}
                                        valueChanged={valueChanged}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container>
                                <Grid item xs={8}>
                                    <Typography>
                                        When you picture funding a plan like this, would you prefer a one-time lump sum,
                                        monthly contributions, or a mix of both?
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <SelectInputElement2
                                        param={{
                                            label: 'Funding Plan',
                                            options: fieldOptions.IntentFunding.options,
                                            optionValues: fieldOptions.IntentFunding.optionValues,
                                            ...prequal.intentFunding,
                                            name: 'intentFunding',
                                        }}
                                        required={required}
                                        valueChanged={valueChanged}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>

                        <BasicInfo parameters={prequal} valueChanged={valueChanged} hideNumbers={useMultiPagePrequal} />
                        <Finances
                            walkthroughData={walkthroughData}
                            parameters={prequal}
                            valueChanged={valueChanged}
                            hideNumbers={useMultiPagePrequal}
                        />
                        <HealthInfo
                            parameters={prequal}
                            healthParameters={health}
                            valueChanged={valueChanged}
                            hideNumbers={useMultiPagePrequal}
                        />
                        <Behavior parameters={prequal} valueChanged={valueChanged} hideNumbers={useMultiPagePrequal} />

                        {historyFormId && (
                            <Grid xs={12} md={3}>
                                <MUIButton
                                    disabled={loading}
                                    variant="outlined"
                                    color="primary"
                                    style={{marginTop: 12, width: '100%'}}
                                    onClick={async () => {
                                        parameterStore.formid = '';
                                        saveForm();
                                    }}
                                >
                                    Save as a form
                                </MUIButton>
                            </Grid>
                        )}
                        <BestFitProduct
                            parameterStore={parameterStore}
                            customerQuoteData={customerQuoteData}
                            onSave={saveForm}
                            loading={loading}
                        />
                    </Card>
                </Grid>
                <Grid
                    item
                    xs={assistPanelExpanded ? 4 : 1}
                    style={{
                        height: '100%',
                        overflowY: 'auto',
                        ...(!assistPanelExpanded && {display: 'flex', justifyContent: 'center'}),
                    }}
                >
                    <AgentAssistPanel
                        tab="prequal"
                        doOnExpand={() => setAssistPanelExpanded(true)}
                        doOnCollapse={() => setAssistPanelExpanded(false)}
                        onValueChangedNotes={(value) => valueChanged({...prequal?.notes, name: 'notes'}, value)}
                    />
                </Grid>
                <Dialog
                    open={saveConfirmationOpen}
                    onClose={() => cancelPendingSave()}
                    aria-labelledby="confirm-dialog-title"
                >
                    <DialogTitle id="confirm-dialog-title">Confirmation</DialogTitle>
                    <DialogContent>
                        <DialogContentText>The SSN is associated with following forms:</DialogContentText>
                        <DialogContentText>
                            <ul>
                                {duplicateSsnForms.map(({formId, email}) => (
                                    <li key={formId}>
                                        {formId} - {email}
                                    </li>
                                ))}
                            </ul>
                        </DialogContentText>
                        <DialogContentText>Please double check before confirming save.</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => cancelPendingSave()} color="primary">
                            Cancel
                        </Button>
                        <Button color="primary" onClick={() => confirmPendingSave()}>
                            Confirm Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </Grid>
        );
    },
);
